import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { PageHeader, ClientLogo } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { Plus, Search, Upload, Download } from "lucide-react";
import { toast } from "sonner";
import { parseApiError } from "@/lib/apiErrors";

const emptyClient = {
  name: "", registered_address: "", cin: "", pan: "", gstin: "",
  tan: "", udhyam: "", date_of_incorporation: "", contact_email: "", contact_phone: "",
};

export default function Clients() {
  const { user } = useAuth();
  const [clients, setClients] = useState([]);
  const [filter, setFilter] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyClient);
  const [importOpen, setImportOpen] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const canCreate = ["Admin", "Manager"].includes(user.role);

  const load = async () => {
    const r = await api.get("/clients");
    setClients(r.data || []);
  };
  useEffect(() => { load(); }, []);

  const submit = async () => {
    try {
      await api.post("/clients", form);
      toast.success("Client created");
      setOpen(false); setForm(emptyClient);
      load();
    } catch (e) {
      toast.error(parseApiError(e, "Failed to create client"));
    }
  };

  const filtered = clients.filter((c) =>
    [c.name, c.cin, c.pan, c.gstin].some((v) => (v || "").toLowerCase().includes(filter.toLowerCase()))
  );

  const downloadTemplate = async () => {
    try {
      const r = await api.get("/clients/import-template", { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url; a.download = "clients_import_template.csv"; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Failed to download template"); }
  };

  const runImport = async () => {
    if (!importFile) { toast.error("Pick a CSV file first"); return; }
    setImporting(true); setImportResult(null);
    try {
      const fd = new FormData();
      fd.append("file", importFile);
      const r = await api.post("/clients/bulk-import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setImportResult(r.data);
      if (r.data.created > 0) toast.success(`Imported ${r.data.created} client${r.data.created === 1 ? "" : "s"}`);
      if (r.data.created === 0 && r.data.skipped > 0) toast.error("No clients imported — see error list");
      load();
    } catch (e) {
      toast.error(parseApiError(e, "Import failed"));
    } finally {
      setImporting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white" data-testid="clients-page">
      <PageHeader
        title="Clients"
        subtitle="Directory"
        action={
          canCreate && (
            <div className="flex items-center gap-2">
              <Dialog open={importOpen} onOpenChange={(v) => { setImportOpen(v); if (!v) { setImportFile(null); setImportResult(null); } }}>
                <DialogTrigger asChild>
                  <Button variant="outline" className="rounded-sm" data-testid="btn-import-clients">
                    <Upload size={14} className="mr-1.5" /> Import CSV
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-2xl rounded-sm" data-testid="dialog-import-clients">
                  <DialogHeader>
                    <DialogTitle style={{ fontFamily: "Outfit" }}>Bulk import clients</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4">
                    <div className="border border-slate-200 bg-slate-50 px-3 py-2.5 rounded-sm text-xs text-slate-600">
                      Upload a CSV file with the following columns (header row required): <span className="font-mono-data text-[#0a2540]">name, registered_address, cin, pan, gstin, tan, udhyam, date_of_incorporation, contact_email, contact_phone</span>.
                      Dates must be <span className="font-mono-data">YYYY-MM-DD</span>. Only <span className="font-medium">name</span> is required.
                      <button onClick={downloadTemplate} className="ml-2 text-[#0a2540] underline hover:no-underline" data-testid="btn-download-template">
                        <Download size={10} className="inline mr-0.5" /> Download template
                      </button>
                    </div>

                    <div>
                      <Label className="text-xs">CSV file</Label>
                      <Input
                        type="file"
                        accept=".csv,text/csv"
                        className="mt-1 rounded-sm"
                        onChange={(e) => { setImportFile(e.target.files?.[0] || null); setImportResult(null); }}
                        data-testid="input-import-csv"
                      />
                    </div>

                    {importResult && (
                      <div className="border border-slate-200 rounded-sm" data-testid="import-result">
                        <div className="flex items-center gap-4 px-3 py-2 bg-slate-50 border-b border-slate-200 text-xs">
                          <span><span className="font-mono-data text-emerald-700" data-testid="import-created">{importResult.created}</span> created</span>
                          <span><span className="font-mono-data text-amber-700" data-testid="import-skipped">{importResult.skipped}</span> skipped</span>
                        </div>
                        {importResult.errors?.length > 0 && (
                          <div className="max-h-48 overflow-y-auto">
                            <table className="w-full text-xs">
                              <thead className="bg-white text-[10px] uppercase tracking-wider text-slate-500">
                                <tr><th className="text-left px-3 py-1.5">Row</th><th className="text-left px-3 py-1.5">Name</th><th className="text-left px-3 py-1.5">Error</th></tr>
                              </thead>
                              <tbody>
                                {importResult.errors.map((e) => (
                                  <tr key={`err-${e.row}-${e.name || ""}`} className="border-t border-slate-100">
                                    <td className="px-3 py-1.5 font-mono-data text-slate-500">{e.row}</td>
                                    <td className="px-3 py-1.5">{e.name || <span className="text-slate-300">—</span>}</td>
                                    <td className="px-3 py-1.5 text-red-600">{e.error}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                  <DialogFooter>
                    <Button variant="outline" className="rounded-sm" onClick={() => setImportOpen(false)} data-testid="btn-import-close">Close</Button>
                    <Button
                      className="bg-[#0a2540] hover:bg-[#0f2f50] rounded-sm"
                      onClick={runImport}
                      disabled={!importFile || importing}
                      data-testid="btn-run-import"
                    >
                      {importing ? "Importing…" : "Import"}
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>

              <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild>
                <Button className="bg-[#0a2540] hover:bg-[#0f2f50] rounded-sm" data-testid="btn-new-client">
                  <Plus size={16} className="mr-1.5" /> New Client
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl rounded-sm" data-testid="dialog-new-client">
                <DialogHeader>
                  <DialogTitle style={{ fontFamily: "Outfit" }}>New Client</DialogTitle>
                </DialogHeader>
                <div className="grid grid-cols-2 gap-4">
                  {[
                    ["name", "Client Name *", "text", ""],
                    ["registered_address", "Registered Address", "text", ""],
                    ["cin", "CIN / LLPIN", "mono", "Standard CIN (21 chars, e.g. U12345KA2020PTC123456) or LLPIN (e.g. AAA-1234) — leave blank if not applicable"],
                    ["pan", "PAN", "mono", "10 chars (e.g. ABCDE1234F)"],
                    ["gstin", "GSTIN", "mono", "15 chars (e.g. 29ABCDE1234F1Z5)"],
                    ["tan", "TAN", "mono", "10 chars (e.g. BLRA12345A)"],
                    ["udhyam", "Udhyam Number", "mono", ""],
                    ["date_of_incorporation", "Date of Incorporation", "date", ""],
                    ["contact_email", "Contact Email", "text", ""],
                    ["contact_phone", "Contact Phone", "text", ""],
                  ].map(([k, label, type, hint]) => (
                    <div key={k} className={k === "registered_address" ? "col-span-2" : ""}>
                      <Label className="text-xs font-medium">{label}</Label>
                      {k === "registered_address" ? (
                        <Textarea
                          className={`mt-1 rounded-sm`}
                          value={form[k]}
                          onChange={(e) => setForm({ ...form, [k]: e.target.value })}
                          data-testid={`input-${k}`}
                        />
                      ) : (
                        <Input
                          className={`mt-1 rounded-sm ${type === "mono" ? "font-mono-data" : ""}`}
                          type={type === "date" ? "date" : "text"}
                          value={form[k]}
                          onChange={(e) => setForm({ ...form, [k]: e.target.value })}
                          data-testid={`input-${k}`}
                        />
                      )}
                      {hint && <div className="mt-1 text-[10px] text-slate-400 leading-snug">{hint}</div>}
                    </div>
                  ))}
                </div>
                <DialogFooter>
                  <Button variant="outline" className="rounded-sm" onClick={() => setOpen(false)} data-testid="btn-cancel">
                    Cancel
                  </Button>
                  <Button className="bg-[#0a2540] hover:bg-[#0f2f50] rounded-sm" onClick={submit} data-testid="btn-create-client">
                    Create Client
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
            </div>
          )
        }
      />

      <div className="px-4 sm:px-8 py-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="relative flex-1 max-w-sm">
            <Search size={14} className="absolute left-2.5 top-2.5 text-slate-400" />
            <Input
              placeholder="Search by name, CIN, PAN, GSTIN…"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              className="pl-8 rounded-sm"
              data-testid="input-search-clients"
            />
          </div>
          <div className="text-xs text-slate-500 font-mono-data">
            {filtered.length} of {clients.length}
          </div>
        </div>

        {/* Mobile: card list */}
        <div className="sm:hidden space-y-2" data-testid="client-cards-mobile">
          {filtered.length === 0 && (
            <div className="border border-slate-200 px-4 py-10 text-center text-xs text-slate-400">No clients yet</div>
          )}
          {filtered.map((c) => (
            <Link key={c.id} to={`/clients/${c.id}`} className="border border-slate-200 px-3 py-2.5 flex items-center gap-3 hover:bg-slate-50" data-testid={`client-card-${c.id}`}>
              <ClientLogo fileId={c.logo_file_id} name={c.name} size={32} />
              <div className="min-w-0 flex-1">
                <div className="text-sm font-semibold text-[#0a2540] truncate">{c.name}</div>
                <div className="flex flex-wrap gap-x-3 gap-y-0 text-[10px] font-mono-data text-slate-500 mt-0.5">
                  {c.cin && <span>CIN {c.cin}</span>}
                  {c.pan && <span>PAN {c.pan}</span>}
                  {c.gstin && <span>GST {c.gstin}</span>}
                </div>
              </div>
            </Link>
          ))}
        </div>

        {/* Desktop: table */}
        <div className="hidden sm:block border border-slate-200 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr className="text-[11px] uppercase tracking-wider text-slate-500">
                <th className="text-left px-4 py-2.5">Client</th>
                <th className="text-left px-4 py-2.5 font-mono-data">CIN</th>
                <th className="text-left px-4 py-2.5 font-mono-data">PAN</th>
                <th className="text-left px-4 py-2.5 font-mono-data">GSTIN</th>
                <th className="text-left px-4 py-2.5">Contact</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && (
                <tr>
                  <td colSpan={5} className="px-4 py-10 text-center text-xs text-slate-400">
                    No clients yet
                  </td>
                </tr>
              )}
              {filtered.map((c) => (
                <tr key={c.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`client-row-${c.id}`}>
                  <td className="px-4 py-3">
                    <Link to={`/clients/${c.id}`} className="flex items-center gap-3 group" data-testid={`client-link-${c.id}`}>
                      <ClientLogo fileId={c.logo_file_id} name={c.name} size={28} />
                      <span className="font-medium text-[#0a2540] group-hover:underline">{c.name}</span>
                    </Link>
                  </td>
                  <td className="px-4 py-3 font-mono-data text-xs text-slate-600">{c.cin || "—"}</td>
                  <td className="px-4 py-3 font-mono-data text-xs text-slate-600">{c.pan || "—"}</td>
                  <td className="px-4 py-3 font-mono-data text-xs text-slate-600">{c.gstin || "—"}</td>
                  <td className="px-4 py-3 text-xs text-slate-600">{c.contact_email || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
